<template>
  <AutoComplete
    :items="items"
    :placeholder="placeholder"
    :disabled="disabled"
    :readonly="readonly"
    :clearable="clearable"
    :clearAfterSelection="clearAfterSelection"
    :loading="loading"
    :size="size"
    :error="error"
    :openOnFocus="openOnFocus"
    :closeOnSelect="closeOnSelect"
    :textField="textField"
    :keySelector="keySelector"
    :filter="filter"
    @focus="$emit('focus', $event)"
    @blur="$emit('blur', $event)"
    @input="$emit('typing', $event)"
    @cleared="onCleared"
    @change="onChange"
    @itemSelected="onItemSelected"
    ref="autocompleteRef"
  >
    <!-- Forward all slots -->
    <template v-for="(_, name) in $slots" #[name]="slotData">
      <slot :name="name" v-bind="slotData || {}"></slot>
    </template>
  </AutoComplete>
</template>

<script setup>
import { ref, watch, computed, onMounted } from 'vue'
import AutoComplete from './AutoComplete.vue'

const props = defineProps({
  modelValue: {
    type: [String, Number],
    default: null
  },
  items: {
    type: Array,
    default: () => []
  },
  keySelector: {
    type: [String, Function],
    default: 'id'
  },
  textField: {
    type: [String, Function],
    default: 'name'
  },
  filter: {
    type: [Function, undefined],
  },
  placeholder: {
    type: String,
    default: 'Search...'
  },
  disabled: {
    type: Boolean,
    default: false
  },
  readonly: {
    type: Boolean,
    default: false
  },
  clearable: {
    type: Boolean,
    default: true
  },
  clearAfterSelection: {
    type: Boolean,
    default: false
  },
  loading: {
    type: Boolean,
    default: false
  },
  size: {
    type: String,
    default: 'md',
    validator: (value) => ['xs', 'sm', 'md', 'lg', 'xl'].includes(value)
  },
  error: {
    type: Boolean,
    default: false
  },
  openOnFocus: {
    type: Boolean,
    default: true
  },
  closeOnSelect: {
    type: Boolean,
    default: true
  }
})

const emit = defineEmits([
  'update:modelValue',
  'change',
  'focus',
  'blur',
  'itemSelected',
  'open',
  'close',
  'typing',
  'cleared'
])

const autocompleteRef = ref(null)

// Find the selected item in items by id
const selectedItem = computed(() => {
  if (props.modelValue === null || props.modelValue === undefined) return null
  
  return props.items.find(item => {
    const itemKey = typeof props.keySelector === 'function'
      ? props.keySelector(item)
      : item[props.keySelector]
    
    return itemKey == props.modelValue // Use loose equality to match string/number
  })
})

// When the component is mounted and there's a selected value
onMounted(() => {
  if (props.modelValue && selectedItem.value) {
    const displayText = typeof props.textField === 'function'
      ? props.textField(selectedItem.value)
      : selectedItem.value[props.textField]
      
    // Set the display text in the autocomplete
    if (autocompleteRef.value) {
      autocompleteRef.value.inputValue = displayText
    }
  }
})

// Watch for external changes to modelValue
watch(() => props.modelValue, (newValue) => {
  if (newValue === null) {
    // Clear the input field
    if (autocompleteRef.value) {
      autocompleteRef.value.inputValue = ''
    }
  } else {
    // Try to find the item with this ID and update the displayed text
    const item = props.items.find(item => {
      const itemKey = typeof props.keySelector === 'function'
        ? props.keySelector(item)
        : item[props.keySelector]
      
      return itemKey == newValue // Use loose equality to match string/number
    })
    
    if (item && autocompleteRef.value) {
      const displayText = typeof props.textField === 'function'
        ? props.textField(item)
        : item[props.textField]
        
      autocompleteRef.value.inputValue = displayText
    }
  }
})

// When an item is selected, emit the ID value
function onItemSelected(item) {
  const selectedId = typeof props.keySelector === 'function'
    ? props.keySelector(item)
    : item[props.keySelector]
    
  emit('update:modelValue', selectedId)
  emit('itemSelected', item)
  emit('change', selectedId)
}

// When text changes without selection
function onChange(text) {
  // If text is cleared or doesn't match any item, emit null
  if (!text) {
    emit('update:modelValue', null)
    emit('change', null)
  }
}

// When the input is cleared
function onCleared() {
  emit('update:modelValue', null)
  emit('cleared')
}
</script>
